import { useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { anonymousToken, pathTemplate } from '../lib/usageTracking';

// Sends staff usage of DaAFYA to Driventra CRM (POST /public/clicks) as the DaAFYA product, so the CRM's usage
// dashboards and agents see which hospitals use which screens. Off unless VITE_DRIVENTRA_TRACKER_KEY holds a
// publishable key (drv_pk_…) with Click events whose allowed websites include this app's address.
// Only screen templates and control names leave the browser (see lib/usageTracking.ts); clicks are kept in memory,
// not storage, so nothing lingers on shared hospital computers.

const KEY = (import.meta.env.VITE_DRIVENTRA_TRACKER_KEY as string | undefined) || '';
const API = ((import.meta.env.VITE_DRIVENTRA_CRM_API as string | undefined) || 'https://app.driventra.com/api').replace(/\/+$/, '');
const SERVICE_CODE = (import.meta.env.VITE_DRIVENTRA_SERVICE_CODE as string | undefined) || 'DAAFYA-HMS';
const ENABLED = /^drv_pk_[a-f0-9]{16}$/.test(KEY);
const MAX_CLICKS = 500;

interface Click {
  timestamp: string;
  element: string;
  classes: string;
  id: string;
  x: number;
  y: number;
}

export default function DriventraUsageTracker() {
  const { user } = useAuth();
  const clicks = useRef<Click[]>([]);
  const byScreen = useRef<Record<string, { click_data: Click[]; click_count: number }>>({});

  useEffect(() => {
    if (!ENABLED || !user?.id || !user.tenant_id) return;
    const identity = {
      logged_in_user_name: user.full_name || user.name || [user.firstname, user.lastname].filter(Boolean).join(' ') || user.username || `User ${user.id}`,
      logged_in_user_unique_id: String(user.id),
      // The hospital; enter the same value as the Unique External ID on its Company in the CRM.
      customer_unique_id: `daafya-tenant-${user.tenant_id}`,
      service_code: SERVICE_CODE,
    };

    const send = () => {
      if (!clicks.current.length) return;
      const payload = {
        ...identity,
        click_data: clicks.current,
        click_count: clicks.current.length,
        url_wise_clicks: byScreen.current,
        timestamp: new Date().toISOString(),
      };
      clicks.current = [];
      byScreen.current = {};
      // keepalive lets the request finish while the page is closing.
      fetch(`${API}/public/clicks`, {
        method: 'POST',
        keepalive: true,
        headers: { 'Content-Type': 'application/json', 'X-Driventra-Key': KEY },
        body: JSON.stringify(payload),
      }).catch(() => undefined);
    };

    const onClick = (event: MouseEvent) => {
      if (clicks.current.length >= MAX_CLICKS) return;
      const target = event.target instanceof Element ? event.target : null;
      const className = target?.getAttribute('class') || '';
      const click: Click = {
        timestamp: new Date().toISOString(),
        element: target?.tagName || '',
        classes: anonymousToken(className),
        id: anonymousToken(target?.id),
        x: event.clientX,
        y: event.clientY,
      };
      const screen = `${window.location.origin}${pathTemplate(window.location.pathname)}`;
      clicks.current.push(click);
      const entry = (byScreen.current[screen] ||= { click_data: [], click_count: 0 });
      entry.click_data.push(click);
      entry.click_count += 1;
    };
    const onHide = () => {
      if (document.visibilityState === 'hidden') send();
    };

    document.addEventListener('click', onClick, true);
    document.addEventListener('visibilitychange', onHide);
    window.addEventListener('pagehide', send);
    const timer = window.setInterval(send, 60_000);
    return () => {
      // Signing out or switching user: send what belongs to this user, then stop.
      send();
      document.removeEventListener('click', onClick, true);
      document.removeEventListener('visibilitychange', onHide);
      window.removeEventListener('pagehide', send);
      window.clearInterval(timer);
    };
  }, [user?.id, user?.tenant_id, user?.full_name, user?.name, user?.firstname, user?.lastname, user?.username]);

  return null;
}
